<template>
    <div v-if="album" class="album-detail overflow-y-auto h-screen-header">
        <div class="album-detail__backdrop" :style="albumBackdropStyle"></div>

        <div ref="heroContainer" class="album-detail__hero">
            <div class="album-detail__art-wrap">
                <img
                    :src="album.image || '/static/dist/img/album_null.jpg'"
                    :alt="album.name"
                    class="album-detail__art"
                >
            </div>

            <div class="album-detail__body">
                <div class="album-detail__headline">
                    <div class="album-detail__kicker">{{ $tt('专辑') }}</div>
                    <h1 class="album-detail__title">{{ album.name }}</h1>
                    <router-link :to="{ name: 'artist', params: { id: album.artistId } }" class="album-detail__artist">
                        {{ album.artist || $tt('未知艺术家') }}
                    </router-link>
                </div>

                <div class="album-detail__stats" aria-label="album metadata">
                    <div class="album-detail__stat">
                        <span>{{ $tt('曲目') }}</span>
                        <strong>{{ trackCount }}</strong>
                    </div>
                    <div class="album-detail__stat">
                        <span>{{ $tt('时长') }}</span>
                        <strong>{{ formatDuration(album.duration) }}</strong>
                    </div>
                    <div class="album-detail__stat">
                        <span>{{ $tt('年份') }}</span>
                        <strong>{{ album.year || '--' }}</strong>
                    </div>
                </div>

                <p v-if="album.comment" class="album-detail__comment" onclick="album_comment.showModal()" :title="album.comment">
                    {{ albumCommentPreview }}
                </p>

                <div class="album-detail__chips">
                    <router-link class="album-detail__chip" :to="{ name: 'artist', params: { id: album.artistId } }">
                        {{ album.artist || $tt('未知艺术家') }}
                    </router-link>
                    <router-link v-if="album.genreId" class="album-detail__chip" :to="{ name: 'genre', params: { id: album.genreId } }">
                        {{ album.genreId }}
                    </router-link>
                    <span v-if="album.year" class="album-detail__chip">{{ album.year }}</span>
                    <span v-for="(item, index) in displayedOtherArtists" :key="index" class="album-detail__chip">
                        {{ item }}
                    </span>
                    <button
                        v-if="hasMoreArtists"
                        class="album-detail__chip album-detail__chip--button"
                        @click="toggleShowAllArtists"
                        :title="showAllArtists ? $tt('收起') : $tt('还有 {count} 个艺术家', { count: remainingArtistsCount })"
                    >
                        {{ showAllArtists ? $tt('收起') : `+${remainingArtistsCount}` }}
                    </button>
                </div>

                <div class="album-detail__actions">
                    <button class="album-detail__play" @click="playNow(album.id)">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l10-6.86a1 1 0 0 0 0-1.72l-10-6.86A1 1 0 0 0 8 5.14z"></path>
                        </svg>
                        <span>{{ $tt('播放') }}</span>
                    </button>
                    <button class="album-detail__action album-detail__action--shuffle" @click="shuffleNow">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17 3h4v4h-2V6.41l-4.59 4.58-1.41-1.41L17.59 5H17V3zM3 6h2.59l5.29 5.29-1.41 1.41L4.41 8H3V6zm10.53 5.47 1.41 1.41L12.41 15.4l1.06 1.06-1.41 1.41L9.59 15.4 3 22v-2.83l5.17-5.17L3 8.83V6l10.53 10.47zM17 17h.59l-4.59-4.58 1.41-1.41L19 15.59V15h2v4h-4v-2z"></path>
                        </svg>
                        <span>{{ $tt('随机') }}</span>
                    </button>
                    <button class="album-detail__action album-detail__action--favorite" @click="addFavouriteAlbum">
                        <svg viewBox="0 0 24 24" :fill="isFavourite ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2">
                            <path d="M12 21s-6.72-4.35-9.2-8.3A5.38 5.38 0 0 1 7.35 4.6 5.2 5.2 0 0 1 12 7.09 5.2 5.2 0 0 1 16.65 4.6a5.38 5.38 0 0 1 4.55 8.1C18.72 16.65 12 21 12 21z"></path>
                        </svg>
                        <span>{{ isFavourite ? $tt('已收藏') : $tt('收藏') }}</span>
                    </button>
                    <button class="album-detail__action album-detail__action--next" @click="setNextInQueue">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M4 5h10v2H4zm0 6h10v2H4zm0 6h10v2H4zm12-7V6l6 5-6 5v-4z"></path>
                        </svg>
                        <span>{{ $tt('下一首') }}</span>
                    </button>
                    <button class="album-detail__action album-detail__action--queue" @click="addToQueue">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M4 6h12v2H4zm0 5h12v2H4zm0 5h8v2H4zm14-5V8h2v3h3v2h-3v3h-2v-3h-3v-2z"></path>
                        </svg>
                        <span>{{ $tt('队列') }}</span>
                    </button>
                    <label for="my-drawer-album" class="album-detail__action album-detail__action--edit" @click="handleEditAlbum" v-if="isSuperuser">
                        {{ $tt('编辑') }}
                    </label>
                    <button class="album-detail__action album-detail__action--danger" onclick="album_delete.showModal()" v-if="isSuperuser">
                        {{ $tt('删除') }}
                    </button>
                </div>
            </div>
        </div>

        <section ref="trackContainer" class="album-detail__tracks">
            <div class="album-detail__tracks-header">
                <div>
                    <h2>{{ $tt('{count} 首歌曲', { count: displayedTracks.length }) }}</h2>
                    <p>{{ $tt('添加于{createdAt}', { createdAt: formatDate(album.created) }) }}</p>
                </div>
                <div v-if="showDiscTabs" class="album-disc-tabs segmented-tabs">
                    <button
                        v-for="tab in discTabs"
                        :key="tab.value"
                        :class="['segmented-tab is-compact', activeDiscTab === tab.value ? 'is-active' : '']"
                        @click="activeDiscTab = tab.value"
                    >
                        {{ tab.label }}
                    </button>
                </div>
            </div>
            <div class="album-detail__track-list">
                <TrackList :tracks="displayedTracks" no-album />
            </div>
        </section>

        <!-- 编辑专辑信息抽屉 - 移到最外层避免 z-index 问题 -->
        <div class="drawer drawer-end" v-if="isSuperuser">
            <input id="my-drawer-album" type="checkbox" class="drawer-toggle" v-model="isEditAlbum" />
            <div class="drawer-side" style="z-index: 9999;">
                <label for="my-drawer-album" aria-label="close sidebar" class="drawer-overlay"></label>
                <div class="p-4 w-80 min-h-full bg-base-200 text-base-content">
                    <div class="font-medium text-xl mb-4">{{ $tt('编辑专辑信息') }}</div>

                    <div class="mb-1">{{ $tt('专辑:') }}</div>
                    <div>
                        <input type="text"
                            v-model="FormData.name"
                            class="input input-bordered input-sm w-full max-w-xs" />
                    </div>
                    <div class="mb-1 mt-1">{{ $tt('专辑封面:') }}</div>
                    <div v-if="reloadImg">
                        <!-- 图片上传 -->
                        <div class="relative">
                            <!-- 图片预览区域 -->
                            <div class="border-2 border-dashed border-gray-300 rounded-lg p-2 text-center hover:border-gray-400 transition-colors">
                                <div v-if="albumImagePreview" class="relative inline-block">
                                    <img :src="albumImagePreview" :alt="$tt('专辑封面')" class="max-w-full max-h-48 rounded-lg shadow-md">
                                    <button @click.stop="removeAlbumImage"
                                        class="absolute -top-2 -right-2 btn btn-circle btn-xs btn-error text-white hover:bg-red-600 z-50"
                                        type="button" style="pointer-events: auto;">
                                        ✕
                                    </button>
                                    <!-- 替换图片按钮 -->
                                    <button @click.stop="triggerAlbumImageReplacement"
                                        class="absolute -bottom-2 -right-2 btn btn-circle btn-xs btn-neutral text-white z-50"
                                        type="button" style="pointer-events: auto;" :title="$tt('替换图片')">
                                        +
                                    </button>
                                </div>
                                <div v-else>
                                    <svg class="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                    <p class="mt-2 text-sm text-gray-500">{{ $tt('点击上传或拖拽图片到此处') }}</p>
                                    <p class="text-xs text-gray-400">{{ $tt('支持 JPG, PNG, GIF (最大 5MB)') }}</p>
                                </div>

                                <!-- 隐藏的文件输入 -->
                                <input ref="albumImageInput" type="file" accept="image/*"
                                    @change="handleAlbumImageChange"
                                    class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                    :disabled="isUploadingAlbumImage" v-show="!albumImagePreview">
                                <!-- 上传进度 -->
                                <div v-if="isUploadingAlbumImage"
                                    class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
                                    <div class="text-center">
                                        <span class="loading loading-spinner loading-md"></span>
                                        <p class="mt-2 text-sm text-gray-600">{{ $tt('上传中...') }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="mb-1 mt-1">{{ $tt('描述:') }}</div>
                    <textarea class="textarea textarea-bordered textarea-xs w-full max-w-xs h-48" placeholder="." v-model="FormData.comment"></textarea>
                    <button class="btn btn-neutral btn-sm btn-wide" @click="handleSave">{{ $tt('保存') }}</button>
                    <button class="btn btn-sm btn-wide btn-primary mt-2" @click="handleSaveMetadata">{{ $tt('保存并更新音乐元数据') }}</button>
                </div>
            </div>
        </div>

        <dialog id="album_delete" class="modal modal-bottom sm:modal-middle">
            <div class="modal-box">
                <h3 class="text-lg font-bold">{{ $tt('删除专辑') }}</h3>
                <div class="py-4 text-gray-400">
                    <div>{{ $tt('删除音乐: 将专辑从音乐收藏中移除，同时删除专辑内的音乐文件。') }}</div>
                    <div>{{ $tt('删除收藏: 将专辑从音乐收藏中移除，但并不会删除音乐文件。') }}</div>
                </div>
                <div class="modal-action">
                    <form method="dialog">
                        <!-- if there is a button in form, it will close the modal -->
                        <button class="btn btn-error text-white btn-sm mr-2" @click="handleDelete('delete_file')">{{ $tt('删除音乐') }}</button>
                        <button class="btn btn-neutral btn-sm mr-2" @click="handleDelete('delete_track')">{{ $tt('删除收藏') }}</button>
                        <button class="btn btn-sm">{{ $tt('取消') }}</button>
                    </form>
                </div>
            </div>
        </dialog>
        <dialog id="album_comment" class="modal">
            <div class="modal-box">
                <h3 class="text-lg font-bold">{{ $tt('专辑简介:') }}{{album.name}}</h3>
                <div class="py-4">
                    <textarea class="textarea textarea-bordered textarea-xs w-full h-96" placeholder="." v-model="album.comment"></textarea>
                </div>
            </div>
            <form method="dialog" class="modal-backdrop">
                <button>close</button>
            </form>
        </dialog>
    </div>
</template>
<script>
    import TrackList from '@/views/library/track/TrackList'
    import {mapGetters} from 'vuex'

    export default {
        components: {
            TrackList
        },
                    data() {
                return {
                    album: null,
                    id: this.$route.params.id,
                    isFavourite: false,
                    FormData: {},
                    artistsData: [],
                    uploadUrl: '/apimt/upload_image/',
                    isEditAlbum: false,
                    // 新增的图片上传相关数据
                    albumImagePreview: null,
                    isUploadingAlbumImage: false,
                    reloadImg: true,
                    // 艺术家显示相关数据
                    showAllArtists: false,
                    maxDisplayedArtists: 3,
                    activeDiscTab: 'all'
                }
            },
        computed: {
            ...mapGetters({
                isSuperuser: 'getIsSuperuser'
            }),
            // 显示的其他艺术家列表
            displayedOtherArtists() {
                if (!this.album?.otherArtist || this.album.otherArtist.length === 0) {
                    return []
                }
                
                if (this.showAllArtists || this.album.otherArtist.length <= this.maxDisplayedArtists) {
                    return this.album.otherArtist
                }
                
                return this.album.otherArtist.slice(0, this.maxDisplayedArtists)
            },
            
            // 是否有更多艺术家
            hasMoreArtists() {
                return this.album?.otherArtist && this.album.otherArtist.length > this.maxDisplayedArtists
            },
            
            // 剩余艺术家数量
            remainingArtistsCount() {
                if (!this.album?.otherArtist) return 0
                return this.album.otherArtist.length - this.maxDisplayedArtists
            },
            trackCount() {
                return this.album?.tracks?.length || 0
            },
            albumCommentPreview() {
                if (!this.album?.comment) return ''
                return this.album.comment.length > 150 ? `${this.album.comment.slice(0, 150)}...` : this.album.comment
            },
            albumBackdropStyle() {
                return {
                    backgroundImage: `url('${this.album?.image || '/static/dist/img/album_null.jpg'}')`
                }
            },
            discGroups() {
                const groups = new Map()
                const tracks = this.album?.tracks || []

                tracks.forEach((track) => {
                    const disc = this.getTrackDiscNumber(track)
                    if (!groups.has(disc)) {
                        groups.set(disc, [])
                    }
                    groups.get(disc).push(track)
                })

                return Array.from(groups.entries())
                    .sort(([a], [b]) => a - b)
                    .map(([disc, tracks]) => ({ disc, tracks }))
            },
            discTabs() {
                return [
                    { value: 'all', label: this.$tt('全部') },
                    ...this.discGroups.map(({ disc }) => ({
                        value: disc,
                        label: disc > 0 ? `Disc ${disc}` : this.$tt('未分碟')
                    }))
                ]
            },
            showDiscTabs() {
                return this.discGroups.length > 1
            },
            displayedTracks() {
                if (this.activeDiscTab === 'all') {
                    return this.album?.tracks || []
                }

                const group = this.discGroups.find((item) => item.disc === this.activeDiscTab)
                return group ? group.tracks : []
            }
        },
        created() {
            this.loadAlbum(this.$route.params.id)
        },
        watch: {
            '$route.params.id'(value) {
                this.loadAlbum(value)
            }
        },
        methods: {
            isValidAlbumId(id) {
                return id !== undefined && id !== null && id !== '' && id !== 'undefined' && id !== 'null'
            },
            async loadAlbum(id) {
                if (!this.isValidAlbumId(id)) {
                    // this.album = null
                    // this.$router.replace({ path: '/albums/recently-added/' })
                    return
                }
                this.id = id
                try {
                    this.album = await this.$api.subsonic.getAlbumDetails(id)
                    this.isFavourite = this.album.favourite
                    this.activeDiscTab = 'all'
                } catch (error) {
                    this.album = null
                    if (this.$myMsg) {
                        this.$myMsg.notify({
                            title: this.$tt('专辑载入失败'),
                            content: error.message || this.$tt('无法打开该专辑'),
                            type: 'error',
                            time: 5000,
                        })
                    }
                    this.$router.replace({ path: '/albums/recently-added/' })
                }
            },
            async playNow(id) {
                const album = await this.$api.subsonic.getAlbumDetails(id)
                return this.$store.dispatch('playTrackList', {
                    tracks: album.tracks
                })
            },
            getTrackDiscNumber(track) {
                const value = track?.discNumber ?? track?.disc_number ?? 0
                const disc = Number(value)
                return Number.isFinite(disc) ? disc : 0
            },
            shuffleNow() {
                return this.$store.dispatch('player/shuffleNow', {
                    tracks: this.album && this.album.tracks ? this.album.tracks : []
                })
            },
            setNextInQueue() {
                if (this.album) {
                    return this.$store.dispatch('player/setNextInQueue', this.album.tracks)
                }
            },
            addToQueue() {
                if (this.album) {
                    return this.$store.dispatch('player/addToQueue', this.album.tracks)
                }
            },
            toggleFavourite() {
                return this.favouriteStore.toggle('album', this.id)
            },
            formatDuration(value) {
                if (!isFinite(value)) {
                    return '∞'
                }
                const minutes = Math.floor(value / 60)
                const seconds = Math.floor(value % 60)
                return (minutes < 10 ? '0' : '') + minutes + ':' + (seconds < 10 ? '0' : '') + seconds
            },
            formatDate(value) {
                if (!value) return '--'
                const date = new Date(value)
                if (Number.isNaN(date.getTime())) {
                    return value
                }
                const year = date.getFullYear()
                const month = `${date.getMonth() + 1}`.padStart(2, '0')
                const day = `${date.getDate()}`.padStart(2, '0')
                return `${year}-${month}-${day}`
            },
            addFavouriteAlbum() {
                if (this.isFavourite) {
                    this.$api.subsonic.removeFavourite(this.album.id, 'album').then((res) => {
                        this.isFavourite = false
                    })
                } else {
                    this.$api.subsonic.addFavourite(this.album.id, 'album').then((res) => {
                        this.isFavourite = true
                    })
                }
            },
            handleSave() {
                this.$api.Task.postAlbum({
                    id: this.id,
                    album_img: this.FormData.album_img,
                    comment: this.FormData.comment,
                    name: this.FormData.name
                }).then((res) => {
                    if (res.result) {
                        this.$myMsg.notify({
                            title: this.$tt('修改成功！'),
                            type: 'success',
                            time: 2000,
                        })
                        this.$api.subsonic.getAlbumDetails(this.id).then((res) => {
                            this.album = res
                        })
                    } else {
                        this.$myMsg.notify({
                            title: this.$tt('创建失败！'),
                            type: 'error',
                            time: 5000,
                        })
                    }
                })
            },
            handleSaveMetadata() {
                this.$api.Task.postAlbum({
                    id: this.id,
                    album_img: this.FormData.album_img,
                    comment: this.FormData.comment,
                    name: this.FormData.name,
                    write_metadata: true
                }).then((res) => {
                    if (res.result) {
                        this.$myMsg.notify({
                            title: this.$tt('修改成功！'),
                            type: 'success',
                            time: 2000,
                        })
                        this.$api.subsonic.getAlbumDetails(this.id).then((res) => {
                            this.album = res
                        })
                        this.isEditAlbum = false
                    } else {
                        this.$myMsg.notify({
                            title: this.$tt('创建失败！'),
                            type: 'error',
                            time: 5000,
                        })
                    }
                })
            },
            handleDelete(mode) {
                this.$api.Task.deleteAlbum({ 'id': this.id, mode }).then((res) => {
                    if (res.result) {
                        this.$myMsg.notify({
                            title: this.$tt('删除成功！'),
                            type: 'success',
                            time: 2000,
                        })
                        this.$router.push({path: '/albums/recently-added/'})
                    } else {
                        this.$myMsg.notify({
                            title: this.$tt('删除失败！'),
                            type: 'error',
                            time: 2000,
                        })
                    }
                })
            },
            handleEditAlbum() {
                if (this.album.image) {
                    this.albumImagePreview = this.album.image
                }
                this.FormData = {
                    name: this.album.name,
                    comment: this.album.comment
                }
            },
            // 渲染图片
            handleRes(response) {
                if (response.result) {
                    this.FormData.album_img = response.data
                    return true
                } else {
                    return false
                }
            },
            // 移除专辑封面
            removeAlbumImage() {
                this.albumImagePreview = null
                this.FormData.album_img = ''
                if (this.$refs.albumImageInput) {
                    this.$refs.albumImageInput.value = ''
                }
            },
            // 触发专辑封面替换
            triggerAlbumImageReplacement() {
                // 直接创建临时input，更可靠
                this.createTempFileInput('album')
            },
            
            // 创建临时文件输入
            createTempFileInput(type) {
                const input = document.createElement('input')
                input.type = 'file'
                input.accept = 'image/*'
                input.style.display = 'none'
                input.onchange = (event) => {
                    if (type === 'album') {
                        this.handleAlbumImageChange(event)
                    }
                    // 清理临时元素
                    if (input.parentNode) {
                        input.parentNode.removeChild(input)
                    }
                }
                document.body.appendChild(input)
                input.click()
            },
            // 处理专辑封面图片选择
            handleAlbumImageChange(event) {
                const file = event.target.files[0]
                if (!file) return
                
                // 验证文件类型
                if (!file.type.startsWith('image/')) {
                    this.$myMsg.notify({
                        title: this.$tt('文件类型错误'),
                        content: this.$tt('请选择图片文件'),
                        type: 'error',
                        time: 2000,
                    })
                    return
                }
                
                // 验证文件大小 (5MB)
                if (file.size > 5 * 1024 * 1024) {
                    this.$myMsg.notify({
                        title: this.$tt('文件过大'),
                        content: this.$tt('图片大小不能超过5MB'),
                        type: 'error',
                        time: 2000,
                    })
                    return
                }
                
                // 显示预览
                const reader = new FileReader()
                reader.onload = (e) => {
                    this.albumImagePreview = e.target.result
                }
                reader.readAsDataURL(file)
                
                // 上传图片
                this.uploadImage(file, 'album')
            },
            // 通用图片上传方法
            uploadImage(file, type) {
                const formData = new FormData()
                formData.append('upload_file', file)
                
                // 设置上传状态
                if (type === 'album') {
                    this.isUploadingAlbumImage = true
                }
                
                // 创建 XMLHttpRequest
                const xhr = new XMLHttpRequest()
                
                // 设置请求头
                xhr.open('POST', this.uploadUrl, true)
                xhr.setRequestHeader('X-CSRFToken', this.getCookie('django_vue_cli_csrftoken'))
                xhr.setRequestHeader('AUTHORIZATION', this.getCookie('AUTHORIZATION'))
                xhr.withCredentials = true
                
                // 处理响应
                xhr.onload = () => {
                    // 重置上传状态
                    if (type === 'album') {
                        this.isUploadingAlbumImage = false
                    }
                    
                    if (xhr.status === 200) {
                        try {
                            const response = JSON.parse(xhr.responseText)
                            if (this.handleRes(response)) {
                                this.$myMsg.notify({
                                    title: this.$tt('上传成功'),
                                    type: 'success',
                                    time: 2000,
                                })
                            } else {
                                    this.$myMsg.notify({
                                        title: this.$tt('上传失败'),
                                        content: response.message || this.$tt('未知错误'),
                                        type: 'error',
                                        time: 2000,
                                    })
                            }
                        } catch (e) {
                            console.log(e)
                            this.$myMsg.notify({
                                title: this.$tt('上传失败'),
                                content: this.$tt('服务器响应格式错误'),
                                type: 'error',
                                time: 2000,
                            })
                        }
                    } else {
                        this.$myMsg.notify({
                            title: this.$tt('上传失败'),
                            content: this.$tt('HTTP错误: {status}', { status: xhr.status }),
                            type: 'error',
                            time: 2000,
                        })
                    }
                }
                
                // 处理错误
                xhr.onerror = () => {
                    if (type === 'album') {
                        this.isUploadingAlbumImage = false
                    }
                    this.$myMsg.notify({
                        title: this.$tt('上传失败'),
                        content: this.$tt('网络错误'),
                        type: 'error',
                        time: 2000,
                    })
                }
                
                // 发送请求
                xhr.send(formData)
            },
            handleCopy(k, v) {
                if (k === 'album_img') {
                    this.albumImagePreview = v
                    this.FormData.album_img = v
                } else {
                    this.FormData[k] = v
                }
            },
            // 切换显示所有艺术家的状态
            toggleShowAllArtists() {
                this.showAllArtists = !this.showAllArtists
            }
        }
    }
</script>

<style scoped>
.album-detail {
    position: relative;
    min-height: 0;
    overflow-x: hidden;
    padding: 0.75rem;
    border-radius: 1rem;
    color: oklch(var(--bc));
    background:
        radial-gradient(circle at 8% 0%, oklch(var(--p) / 0.16), transparent 34rem),
        linear-gradient(135deg, oklch(var(--b1)), oklch(var(--b2)));
}

.album-detail__backdrop {
    position: absolute;
    inset: -2rem;
    background-position: center 24%;
    background-size: cover;
    filter: blur(46px) saturate(1.1);
    opacity: 0.14;
    transform: scale(1.06);
    pointer-events: none;
}

.album-detail__hero,
.album-detail__tracks {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 1480px;
    margin-left: auto;
    margin-right: auto;
    border: 1px solid oklch(var(--bc) / 0.08);
    background: oklch(var(--b1) / 0.74);
    box-shadow: 0 18px 46px oklch(var(--bc) / 0.08);
    backdrop-filter: blur(18px);
}

.album-detail__hero {
    display: grid;
    grid-template-columns: minmax(10rem, 15rem) minmax(0, 1fr);
    gap: 1.3rem;
    align-items: center;
    padding: 1rem;
    border-radius: 1.3rem;
}

.album-detail__art-wrap {
    width: 100%;
    aspect-ratio: 1;
    padding: 0.45rem;
    border-radius: 1.05rem;
    background: linear-gradient(180deg, oklch(var(--b1) / 0.9), oklch(var(--b2) / 0.72));
    box-shadow: 0 22px 40px oklch(var(--bc) / 0.12);
}

.album-detail__art {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 0.82rem;
}

.album-detail__body {
    min-width: 0;
    display: grid;
    gap: 0.75rem;
}

.album-detail__headline {
    min-width: 0;
}

.album-detail__kicker {
    font-size: 0.78rem;
    font-weight: 800;
    color: oklch(var(--bc) / 0.52);
}

.album-detail__title {
    margin: 0.2rem 0 0;
    max-width: 18ch;
    font-size: clamp(1.7rem, 4vw, 3.4rem);
    line-height: 0.96;
    font-weight: 850;
    letter-spacing: 0;
    color: oklch(var(--bc));
    word-break: break-word;
}

.album-detail__artist {
    display: inline-flex;
    max-width: 100%;
    margin-top: 0.55rem;
    color: oklch(var(--bc) / 0.66);
    font-size: 0.98rem;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.album-detail__stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.55rem;
    max-width: 34rem;
}

.album-detail__stat {
    min-width: 0;
    padding: 0.72rem 0.82rem;
    border-radius: 0.9rem;
    background: oklch(var(--b2) / 0.78);
}

.album-detail__stat span,
.album-detail__tracks-header p {
    display: block;
    color: oklch(var(--bc) / 0.54);
    font-size: 0.76rem;
    font-weight: 700;
}

.album-detail__stat strong {
    display: block;
    margin-top: 0.2rem;
    color: oklch(var(--bc));
    font-size: 1rem;
    line-height: 1.1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.album-detail__comment {
    max-width: 70ch;
    margin: 0;
    color: oklch(var(--bc) / 0.64);
    font-size: 0.92rem;
    line-height: 1.55;
    cursor: pointer;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.album-detail__comment:hover,
.album-detail__artist:hover {
    color: oklch(var(--p));
}

.album-detail__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
}

.album-detail__chip {
    min-width: 0;
    max-width: 12rem;
    min-height: 2rem;
    display: inline-flex;
    align-items: center;
    border: 0;
    border-radius: 999px;
    padding: 0.42rem 0.78rem;
    background: oklch(var(--b2) / 0.9);
    color: oklch(var(--bc) / 0.72);
    font-size: 0.78rem;
    font-weight: 800;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.album-detail__chip--button {
    cursor: pointer;
}

.album-detail__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.55rem;
}

.album-detail__play,
.album-detail__action {
    min-height: 2.55rem;
    border: 0;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    padding: 0 1rem;
    font-size: 0.86rem;
    font-weight: 850;
    white-space: nowrap;
    transition: transform 0.18s ease, background 0.18s ease;
}

.album-detail__play:active,
.album-detail__action:active {
    transform: translateY(1px) scale(0.99);
}

.album-detail__play {
    background: oklch(var(--n));
    color: oklch(var(--nc));
    box-shadow: 0 14px 28px oklch(var(--bc) / 0.18);
}

.album-detail__action {
    background: oklch(var(--b2) / 0.94);
    color: oklch(var(--bc) / 0.78);
}

.album-detail__action--danger {
    color: oklch(var(--er));
}

.album-detail__play svg,
.album-detail__action svg {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
}

.album-detail__tracks {
    margin-top: 0.85rem;
    border-radius: 1.15rem;
    overflow: hidden;
    padding: 0.85rem;
}

.album-detail__tracks-header {
    position: sticky;
    top: -0.85rem;
    z-index: 6;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin: -0.85rem -0.85rem 0.65rem;
    padding: 0.85rem;
    background: oklch(var(--b1) / 0.9);
    border-bottom: 1px solid oklch(var(--bc) / 0.06);
    backdrop-filter: blur(16px);
}

.album-detail__track-list {
    min-width: 0;
    overflow: hidden;
    border-radius: 0.95rem;
}

.album-detail__track-list ::v-deep .track-list {
    max-width: 100%;
    overflow-x: hidden;
    border-radius: 0.95rem;
}

.album-detail__track-list ::v-deep .track-list table {
    width: 100%;
    min-width: 0 !important;
    table-layout: fixed;
}

.album-detail__track-list ::v-deep .track-list th,
.album-detail__track-list ::v-deep .track-list td {
    min-width: 0;
}

.album-detail__tracks-header h2 {
    margin: 0;
    color: oklch(var(--bc));
    font-size: 1rem;
    font-weight: 850;
}

.album-detail__tracks-header p {
    margin: 0.16rem 0 0;
}

.album-disc-tabs {
    flex-shrink: 0;
    flex-wrap: wrap;
    width: auto;
    max-width: 100%;
    overflow-x: hidden;
    background: oklch(var(--b2) / 0.92);
    backdrop-filter: blur(12px);
}

.album-disc-tabs .segmented-tab {
    white-space: nowrap;
}

@media (prefers-reduced-motion: reduce) {
    .album-detail__hero,
    .album-detail__play,
    .album-detail__action {
        transition: none;
    }
}

@media (max-width: 768px) {
    .album-detail {
        padding: 0.65rem;
        border-radius: 0;
    }

    .album-detail__hero {
        grid-template-columns: 5.75rem minmax(0, 1fr);
        gap: 0.85rem;
        align-items: start;
        padding: 0.75rem;
        border-radius: 1.35rem;
    }

    .album-detail__body {
        gap: 0.55rem;
    }

    .album-detail__kicker,
    .album-detail__comment,
    .album-detail__chips,
    .album-detail__stats {
        display: none;
    }

    .album-detail__title {
        max-width: none;
        font-size: clamp(1.25rem, 7vw, 1.85rem);
        line-height: 1.04;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .album-detail__artist {
        margin-top: 0.35rem;
        font-size: 0.86rem;
    }

    .album-detail__actions {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 0.45rem;
    }

    .album-detail__play,
    .album-detail__action {
        min-width: 0;
        min-height: 2.35rem;
        padding: 0 0.55rem;
        font-size: 0.76rem;
    }

    .album-detail__play {
        grid-column: span 1;
    }

    .album-detail__tracks {
        padding: 0.65rem;
        border-radius: 1.05rem;
    }

    .album-detail__tracks-header {
        display: block;
        top: -0.65rem;
        margin: -0.65rem -0.65rem 0.55rem;
        padding: 0.65rem;
    }

    .album-disc-tabs {
        margin-top: 0.55rem;
        width: 100%;
    }
}

@media (max-width: 420px) {
    .album-detail__actions {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

</style>
